import React, { useState, useEffect } from 'react';
import {
  MenuItemLink,
  useTranslate,
  useGetIdentity,
  useLogout,
  Menu,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import { Box, Collapse } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ListIcon from '@mui/icons-material/List';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArchiveIcon from '@mui/icons-material/Archive';
import BlockIcon from '@mui/icons-material/Block';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const CustomMenu = (props) => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const logout = useLogout();
  const location = useLocation();
  const role = localStorage.getItem('user_role');
  const isAdmin = role === 'admin';

  // Check if we're on dashboard (either "/" or "/dashboard")
  const isDashboardActive = location.pathname === '/' || location.pathname === '/dashboard';
  const isDashboardV2Active = location.pathname === '/dashboard-v2';

  // Stan do zarządzania rozwinięciem sekcji - tylko jedna sekcja może być otwarta
  const [openSection, setOpenSection] = useState('bidders'); // domyślnie otwarta sekcja Oferenci

  useEffect(() => {
    if (location.pathname.startsWith('/tenders/') || location.pathname === '/tenders') {
      setOpenSection('tenders');
    } else if (location.pathname.startsWith('/bidders/') || location.pathname === '/bidders') {
      setOpenSection('bidders');
    } else if (
      location.pathname.startsWith('/admin/tender-stats') ||
      location.pathname.startsWith('/admin/bidder-stats')
    ) {
      setOpenSection('devtools');
    } else if (location.pathname.startsWith('/predictions/') || location.pathname === '/predictions') {
      setOpenSection('predictions');
    } else if (location.pathname.startsWith('/myAccount/') || location.pathname.startsWith('/change-password') || location.pathname.startsWith('/edit-details')) {
      setOpenSection('account');
    }
  }, [location.pathname]);

  const toggleSection = (section) => {
    // Jeśli kliknięta sekcja jest już otwarta, zamknij ją
    // W przeciwnym razie otwórz ją (zamykając inne)
    setOpenSection((prev) => (prev === section ? null : section));
  };

  return (
    <Box
      bgcolor="white"
      sx={{
        '& .RaMenu-root': {
          marginTop: 2,
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 72px)',
          paddingTop: 2,
        },
      }}
    >
      <Menu {...props} bgcolor="white">
        <Box flex={1} display="flex" flexDirection="column" bgcolor="white">
          <Box flex={1}>
            {/* Dashboard Section */}
            <MenuItemLink
              to="/dashboard"
              primaryText="Dashboard"
              leftIcon={<DashboardIcon />}
              sx={{
                borderRadius: 1,
                margin: '4px 8px',
                padding: '8px 12px',
                minHeight: '40px',
                backgroundColor: isDashboardActive ? 'rgba(227, 24, 55, 0.08)' : 'transparent',
                color: isDashboardActive ? '#E31837' : '#333333',
                fontWeight: isDashboardActive ? 600 : 500,
                borderLeft: isDashboardActive ? '3px solid #E31837' : '3px solid transparent',
                paddingLeft: isDashboardActive ? '9px' : '12px',
                '&:hover': {
                  backgroundColor: 'rgba(227, 24, 55, 0.08)',
                },
                '&.RaMenuItemLink-active': {
                  backgroundColor: 'rgba(227, 24, 55, 0.08)',
                },
              }}
            />

            <MenuItemLink
              to="/dashboard-v2"
              primaryText="Dashboard (nowy)"
              leftIcon={<DashboardIcon />}
              sx={{
                borderRadius: 1,
                margin: '4px 8px',
                padding: '8px 12px',
                minHeight: '40px',
                backgroundColor: isDashboardV2Active ? 'rgba(227, 24, 55, 0.08)' : 'transparent',
                color: isDashboardV2Active ? '#E31837' : '#333333',
                fontWeight: isDashboardV2Active ? 600 : 500,
                borderLeft: isDashboardV2Active ? '3px solid #E31837' : '3px solid transparent',
                paddingLeft: isDashboardV2Active ? '9px' : '12px',
                '&:hover': { backgroundColor: 'rgba(227, 24, 55, 0.08)' },
                '&.RaMenuItemLink-active': { backgroundColor: 'rgba(227, 24, 55, 0.08)' },
              }}
            />

            {/* Tenders Section with Submenu */}
            <Box>
              <Box
                onClick={() => toggleSection('tenders')}
                sx={{
                  borderRadius: 1,
                  margin: '4px 8px',
                  padding: '8px 12px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListIcon sx={{ marginRight: 1, fontSize: '1.25rem' }} />
                  {translate('resources.tenders.name')}
                </Box>
                {openSection === 'tenders' ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </Box>
              <Collapse in={openSection === 'tenders'}>
                <MenuItemLink
                  to="/tenders"
                  primaryText={translate('resources.tenders.name')}
                  leftIcon={<ListIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/tenders/processed"
                  primaryText={translate('resources.tenders.processed')}
                  leftIcon={<CheckCircleIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/tenders/archived"
                  primaryText={translate('resources.tenders.archived')}
                  leftIcon={<ArchiveIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/tenders/failed"
                  primaryText={translate('resources.tenders.failed')}
                  leftIcon={<ErrorIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
              </Collapse>
            </Box>
            {/* Main Entities Section */}
            <Box>
              {/* Bidders Section with Submenu */}
              <Box
                onClick={() => toggleSection('bidders')}
                sx={{
                  borderRadius: 1,
                  margin: '4px 8px',
                  padding: '8px 12px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <GroupIcon sx={{ marginRight: 1, fontSize: '1.25rem' }} />
                  {translate('resources.bidders.name')}
                </Box>
                {openSection === 'bidders' ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </Box>
              <Collapse in={openSection === 'bidders'}>
                <MenuItemLink
                  to="/bidders/all"
                  primaryText={translate(
                    'resources.bidders.all',
                    'Wszyscy oferenci'
                  )}
                  leftIcon={<GroupIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/bidders/archived"
                  primaryText={translate(
                    'resources.bidders.archived',
                    'Archiwalni oferenci'
                  )}
                  leftIcon={<ArchiveIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/bidders/deleted"
                  primaryText={translate(
                    'resources.bidders.deleted',
                    'Usunięci oferenci'
                  )}
                  leftIcon={<ArchiveIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/bidders/blocked"
                  primaryText={translate(
                    'resources.bidders.blocked',
                    'Zablokowani oferenci'
                  )}
                  leftIcon={<ErrorIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/workflow/failed-bidder-workflows"
                  primaryText={translate(
                    'resources.workflow.bidders_with_error',
                    'Oferenci z błędami'
                  )}
                  leftIcon={<ErrorIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/bidders/leads"
                  primaryText={translate(
                    'resources.bidders.leads',
                    'Oferenci Leady'
                  )}
                  leftIcon={<TrendingUpIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                      color: '#E31837',
                      fontWeight: 600,
                      borderLeft: '3px solid #E31837',
                      paddingLeft: '25px',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/bidders/marketing-consent"
                  primaryText={translate(
                    'resources.bidders.marketing_consent',
                    'Oferenci z sukcesem'
                  )}
                  leftIcon={<EmojiEventsIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/bidders/successful"
                  primaryText={translate(
                    'resources.bidders.successful',
                    'Oferenci zwycięzcy'
                  )}
                  leftIcon={<EmojiEventsIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
              </Collapse>
            </Box>

            {/* Ordering Parties Section - Always visible, outside of Bidders collapse */}
            <MenuItemLink
              to="/ordering-parties"
              primaryText={translate('resources.buyers.name')}
              leftIcon={<BusinessIcon />}
              sx={{
                borderRadius: 1,
                margin: '4px 8px',
                padding: '8px 12px',
                minHeight: '40px',
                '&.RaMenuItemLink-active': {
                  backgroundColor: 'rgba(227, 24, 55, 0.08)',
                  color: '#E31837',
                  fontWeight: 600,
                  borderLeft: '3px solid #E31837',
                  paddingLeft: '9px',
                },
                '&:hover': {
                  backgroundColor: 'rgba(227, 24, 55, 0.08)',
                },
              }}
            />

            {/* Predictions Section with Submenu */}
            <Box>
              <Box
                onClick={() => toggleSection('predictions')}
                sx={{
                  borderRadius: 1,
                  margin: '4px 8px',
                  padding: '8px 12px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AnalyticsIcon sx={{ marginRight: 1, fontSize: '1.25rem' }} />
                  {translate('resources.predictions.name', 'Predykcje')}
                </Box>
                {openSection === 'predictions' ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </Box>
              <Collapse in={openSection === 'predictions'}>
                <MenuItemLink
                  to="/predictions/orders"
                  primaryText={translate('resources.predictions.orders', 'Zamówienia')}
                  leftIcon={<AssignmentIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': { backgroundColor: 'rgba(227, 24, 55, 0.08)' },
                  }}
                />
                <MenuItemLink
                  to="/predictions/model-stats"
                  primaryText="Skuteczność modelu"
                  leftIcon={<TrendingUpIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': { backgroundColor: 'rgba(227, 24, 55, 0.08)' },
                  }}
                />
              </Collapse>
            </Box>

            {/* Admin Section */}
            {isAdmin && (
              <Box>
                <MenuItemLink
                  to="/users"
                  primaryText={translate('resources.users.name')}
                  leftIcon={<PeopleIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                {/* Notifications Section */}
                <MenuItemLink
                  to="/notifications"
                  primaryText={translate('resources.notifications.name')}
                  leftIcon={<NotificationsIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/email-templates"
                  primaryText={translate('resources.emailTemplates.name')}
                  leftIcon={<EmailIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/email/messages"
                  primaryText={translate('resources.email.messages.name')}
                  leftIcon={<EmailIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
              </Box>
            )}

            {/* Dev/Admin Section — localhost only */}
            {['localhost', '127.0.0.1'].includes(window.location.hostname) && (
              <Box>
                <Box
                  onClick={() => toggleSection('devtools')}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: 'text.secondary',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ marginRight: 1, fontSize: '1.25rem' }} />
                    Admin
                  </Box>
                  {openSection === 'devtools' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
                <Collapse in={openSection === 'devtools'}>
                  <MenuItemLink
                    to="/admin/tender-stats"
                    primaryText="Monitoring przetargów"
                    leftIcon={<AnalyticsIcon />}
                    sx={{
                      borderRadius: 1,
                      margin: '4px 8px',
                      padding: '8px 12px 8px 28px',
                      minHeight: '40px',
                      '&.RaMenuItemLink-active': { backgroundColor: 'rgba(227, 24, 55, 0.08)' },
                    }}
                  />
                  <MenuItemLink
                    to="/admin/bidder-stats"
                    primaryText="Monitoring oferentów"
                    leftIcon={<AnalyticsIcon />}
                    sx={{
                      borderRadius: 1,
                      margin: '4px 8px',
                      padding: '8px 12px 8px 28px',
                      minHeight: '40px',
                      '&.RaMenuItemLink-active': { backgroundColor: 'rgba(227, 24, 55, 0.08)' },
                    }}
                  />
                </Collapse>
              </Box>
            )}

            {/* Account Section with Submenu */}
            <Box
              sx={{
                borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <Box
                onClick={() => toggleSection('account')}
                sx={{
                  borderRadius: 1,
                  margin: '4px 8px',
                  padding: '8px 12px',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountCircleIcon
                    sx={{ marginRight: 1, fontSize: '1.25rem' }}
                  />
                  {translate('resources.myAccount.name')}
                </Box>
                {openSection === 'account' ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </Box>
              <Collapse in={openSection === 'account'}>
                <MenuItemLink
                  to="/myAccount/edit"
                  primaryText={translate('resources.editDetails.name')}
                  leftIcon={<EditIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
                <MenuItemLink
                  to="/change-password"
                  primaryText={translate('resources.changePassword.name')}
                  leftIcon={<LockIcon />}
                  sx={{
                    borderRadius: 1,
                    margin: '4px 8px',
                    padding: '8px 12px 8px 28px',
                    minHeight: '40px',
                    '&.RaMenuItemLink-active': {
                      backgroundColor: 'rgba(227, 24, 55, 0.08)',
                    },
                  }}
                />
              </Collapse>
            </Box>
          </Box>

          {/* Logout Section */}
          <Box
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              paddingTop: 2,
              paddingBottom: 2,
              marginTop: 'auto',
            }}
          >
            <MenuItemLink
              to="#"
              primaryText={translate('ra.auth.logout')}
              leftIcon={<LogoutIcon />}
              onClick={(e) => {
                e.preventDefault();
                logout();
              }}
              sx={{
                borderRadius: 1,
                margin: '4px 8px',
                padding: '8px 12px',
                minHeight: '40px',
                color: '#E31837',
                '&:hover': {
                  backgroundColor: '#fff3f4',
                },
              }}
            />
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default CustomMenu;
